import emojiData from "emoji-datasource/emoji.json";

interface EmojiVariationEntry {
  unified: string;
  non_qualified?: string | null;
}

interface EmojiDatasourceEntry {
  unified: string;
  non_qualified?: string | null;
  short_name: string;
  short_names: string[];
  skin_variations?: Record<string, EmojiVariationEntry>;
}

const shortcodeByUnified = new Map<string, string>();
const unifiedByShortcode = new Map<string, string>();
const unifiedByNativeEmoji = new Map<string, string>();

const toUnifiedKey = (unified?: string | null) => unified?.toLowerCase();

export const unifiedToEmoji = (unified: string) =>
  unified
    .split("-")
    .map((codePoint) => String.fromCodePoint(Number.parseInt(codePoint, 16)))
    .join("");

const registerShortcode = (shortcode: string, unified: string) => {
  const normalizedShortcode = shortcode.trim().toLowerCase();

  if (!normalizedShortcode) {
    return;
  }

  unifiedByShortcode.set(normalizedShortcode, unified);
  unifiedByShortcode.set(normalizedShortcode.replace(/-/g, "_"), unified);
  unifiedByShortcode.set(normalizedShortcode.replace(/_/g, "-"), unified);
};

const registerUnified = (shortcode: string, unified?: string | null) => {
  const unifiedKey = toUnifiedKey(unified);

  if (!unifiedKey) {
    return;
  }

  shortcodeByUnified.set(unifiedKey, shortcode);
  unifiedByNativeEmoji.set(unifiedToEmoji(unifiedKey), unifiedKey);
};

for (const entry of emojiData as EmojiDatasourceEntry[]) {
  const unifiedKey = toUnifiedKey(entry.unified);

  if (!unifiedKey) {
    continue;
  }

  registerShortcode(entry.short_name, unifiedKey);
  entry.short_names.forEach((shortcode) => registerShortcode(shortcode, unifiedKey));

  registerUnified(entry.short_name, entry.unified);
  registerUnified(entry.short_name, entry.non_qualified);

  Object.values(entry.skin_variations ?? {}).forEach((variation) => {
    registerUnified(entry.short_name, variation.unified);
    registerUnified(entry.short_name, variation.non_qualified);
  });
}

export const getEmojiShortcodeByUnified = (unified?: string | null): string | undefined => {
  const unifiedKey = toUnifiedKey(unified);

  if (!unifiedKey) {
    return undefined;
  }

  return shortcodeByUnified.get(unifiedKey);
};

export const resolveEmojiUnifiedId = (emoji: string): string | undefined => {
  const normalizedEmoji = emoji.trim();

  if (!normalizedEmoji) {
    return undefined;
  }

  const normalizedUnified = normalizedEmoji.toLowerCase();
  if (shortcodeByUnified.has(normalizedUnified)) {
    return normalizedUnified;
  }

  if (normalizedEmoji.includes(":")) {
    const normalizedShortcode = normalizedEmoji.replace(/:/g, "").toLowerCase();
    return unifiedByShortcode.get(normalizedShortcode);
  }

  return unifiedByNativeEmoji.get(normalizedEmoji);
};
